// Wire contract of the workbook index (sculpt-api `sculpt/workbook/payload.py` + `index.py`): the upload job the
// add-in runs at pane open and the status it polls. Keep in sync with the backend.

/** One cell of a sheet payload: [row, col, value, formula, r1c1, fmt, style]. Rows / cols are 1-based. */
export type IndexCell = [number, number, unknown, string | null, string | null, string | null, IndexCellStyle | null];

export interface IndexCellStyle {
  /** Bold label. */
  b?: 1;
  /** Indent level of the label cell. */
  i?: number;
  /** Font colour "RRGGBB" when not the default. */
  c?: string;
  /** Solid fill "RRGGBB". */
  f?: string;
}

export interface IndexSheetPayload {
  name: string;
  position: number;
  visibility: string;
  used_range: string | null;
  fingerprint: string | null;
  tables: { name: string; ref: string }[];
  merged: string[];
  hidden_rows: number[];
  hidden_cols: number[];
  /** The sheet's data validation rules (drop-down lists among them), read over its used range. */
  validations: IndexValidation[];
  charts: number;
  cells: IndexCell[];
}

/** A data validation rule: its areas sheet-less and space-separated (`D23 F5:F9`), its type (`list`, `whole`,
 *  `decimal`, `date`, `time`, `text_length`, `custom`) and a list's source as Office gives it (`Yes,No`,
 *  `=$F$7:$I$7`), null for the other types. */
export interface IndexValidation {
  at: string;
  type: string;
  source: string | null;
}

/** A sheet captured again only for its recalculated values (`IndexJobStarted.values_only`): `[row, col, value]` for
 *  every non-blank value; the server takes the rest from the previous version, which must hold `fingerprint`
 *  (else 409 `values_only_refused`, and the whole sheet is uploaded). */
export interface IndexSheetValues {
  name: string;
  position: number;
  visibility: string;
  fingerprint: string | null;
  values_only: true;
  cells: [number, number, unknown][];
}

export interface IndexSheetAnnouncement {
  name: string;
  position: number;
  visibility: string;
  fingerprint?: string | null;
}

export interface IndexJobStart {
  workbook_name: string;
  sheets: IndexSheetAnnouncement[];
  names: Record<string, string>;
  /** Where the file lives, when the host tells us. The workbook id is a custom document property and a
   *  custom property only survives a save, so an unsaved file comes back under a new id; the URL lets the
   *  server hand it back its own index instead of building another one. */
  document_url?: string | null;
  /** The add-in could not write the id property into the file (D16): no identity question. */
  read_only?: boolean;
  /** The user closed the identity question: this one start goes ahead and records nothing (D5). */
  identity_ack?: 'dismissed';
  /** The model picked on the intro: the server runs the first index's model pass on it (422 `unknown_model`,
   *  403 `model_not_allowed`, as `/chat`). */
  model?: string;
  /** Excel's iterative calculation setting, sent with every start: the server keeps it with the version, and the
   *  staff sandbox computes the workbook's circular references with it (sculpt-api PLAN-sandbox.md D3a). Left out
   *  when the host could not read it. */
  iterative_calculation?: IterativeCalculation;
}

export interface IterativeCalculation {
  enabled: boolean;
  max_iterations: number;
  max_change: number;
}

/** The identity question (sculpt-api PLAN-workbook-identity.md §4.2): the file is announced from a path its
 *  workbook does not know. No path is ever sent, only whether the name and the folder changed (D19). */
export interface IdentityQuestion {
  known_name: string;
  name_changed: boolean;
  path_changed: boolean;
  /** `other`: the file belongs to another user's workbook. */
  owner: 'self' | 'other';
  /** owner `other`: 1 = one "My own copy" answer, 2 = "new version" and "other workbook" (D7). */
  copy_answers: 1 | 2;
  /** Another of the caller's workbooks already knows this path (the unsaved fork, §4.3): offered first. */
  resume: { workbook_id: string; name: string; parent_name: string | null; forked_at: string | null } | null;
  /** The file carries an id the server does not know, and its sheets differ from those of the workbook last read at
   *  this place: offered (`resume`) rather than taken over silently. Answers: that workbook, or another one. */
  lost_id?: boolean;
}

export type IdentityAnswer = 'same' | 'fork' | 'other' | 'resume' | 'dismiss';

export interface IndexJobStarted {
  /** null when the server answered `up_to_date`: no job was opened. */
  job_id: string | null;
  /** The id the server answers under: the one announced, or the one this file is already known by when it
   *  lost its own (same URL). The pane re-points the file to it. */
  workbook_id?: string;
  version: number;
  expected: string[];
  /** Of `expected`, the sheets captured again only because they read a sheet that changed: their values alone are
   *  uploaded (`IndexSheetValues`). Absent against an older server. */
  values_only?: string[];
  /** The stored index already is what the announcement describes: nothing to capture, upload or commit. */
  up_to_date?: boolean;
  /** Asked instead of a job: nothing is captured until it is answered (D6). */
  identity?: IdentityQuestion | null;
}

export interface IndexSheetStatus {
  name: string;
  fingerprint: string | null;
}

export interface IndexStatus {
  workbook_id: string;
  state: 'none' | 'indexing' | 'ready' | 'failed';
  version: number;
  indexed_at: string | null;
  sheets: IndexSheetStatus[];
  /** The counts of the version: the catalog's zones, units and edges once it is built. */
  summary: { sheets: number; cells: number; zones: number; units: number; edges: number };
  /** `reason`: why a `failed` pass failed when the workbook itself is the cause (past what one build may read,
   *  sculpt-api AUD-08); null for any other failure. */
  model_pass: { state: 'pending' | 'running' | 'done' | 'failed' | 'skipped'; done: number; total: number; cost_usd: number; reason: string | null };
  error: string | null;
  job: { job_id: string; expected: string[]; received: string[] } | null;
}

/** `detail.code` of a 409 on POST /agent-v2/chat: the index must be (re)built before the turn. `index_failed`: the
 *  catalog the turns read could not be built (sculpt-api D28) — a job start on the unchanged workbook makes the
 *  server build it again, and the turn waits for it like for any rebuild. */
export const INDEX_REQUIRED_CODES = ['workbook_not_indexed', 'index_stale', 'index_failed'] as const;
/** `detail.code` of a 409 on POST /agent-v2/chat: the index is there, the server has not finished understanding
 *  the version it serves — the catalog of a version just committed is still being built, and every version waits
 *  for its own (sculpt-api D28), not only the first. Nothing to rebuild: the turn waits for the pass and goes
 *  again (`ChatSession.awaitUnderstanding` → `WorkbookIndexer.awaitUnderstood`). */
export const INDEX_UNDERSTANDING_CODE = 'index_not_understood';
